import { sendGAEvent } from './gaConsent';

/**
 * Utility function to track button clicks in Google Analytics
 * @param {string} category
 * @param {string} action
 * @param {string} label
 * @param {Object} additionalParams
 */
export const trackButtonClick = (category, action = 'click', label, additionalParams = {}) => {
  try {
    sendGAEvent({
      category,
      action,
      label,
      ...additionalParams,
    });
  } catch (error) {
    console.warn('Analytics tracking error:', error);
  }
};

/**
 * Track home page main button clicks with unique IDs
 */
export const trackHomeButtonClick = (buttonName, buttonId) => {
  trackEvent({
    name: 'home_cta_click',
    buttonId,
    label: buttonName,
    page: 'home',
    section: 'hero',
    feature: 'home',
    eventCategory: 'home',
  });
};

/**
 * Track feedback button actions
 */
export const trackFeedbackAction = action => {
  trackEvent({
    name: action,
    buttonId: `feedback_${action}`,
    label: 'Feedback button',
    page: 'global',
    section: 'feedback',
    feature: 'feedback',
    eventCategory: 'feedback',
  });
};

/**
 * Track browse all tab clicks
 */
export const trackBrowseTabClick = tabName => {
  trackEvent({
    name: 'tab_click',
    buttonId: `browse_tab_${tabName}`,
    label: tabName,
    page: 'browse',
    section: 'tabs',
    feature: 'browse',
    eventCategory: 'browse',
  });
};

/**
 * Track filter management actions
 */
export const trackFilterAction = action => {
  trackEvent({
    name: action,
    buttonId: `filters_${action}`,
    label: 'Manage filters',
    page: 'search_results',
    section: 'filters',
    feature: 'filters',
    eventGroup: 'search',
    eventCategory: 'search',
  });
};

/**
 * Track save filter management actions
 */
export const trackSaveFilterAction = action => {
  trackEvent({
    name: action,
    buttonId: `save_filters_${action}`,
    label: 'Save filters',
    page: 'search_results',
    section: 'filters',
    feature: 'filters',
    eventGroup: 'search',
    eventCategory: 'search',
  });
};

/**
 * Track enrichment analysis actions
 */
export const trackEnrichmentAction = (action, source = '') => {
  trackEvent({
    name: action,
    buttonId: source ? `enrichment_${action}_${source}` : `enrichment_${action}`,
    label: source ? `${action}_${source}` : action,
    page: 'enrichment',
    section: 'workflow',
    feature: 'enrichment',
    eventCategory: 'enrichment',
  });
};

export const slugifyTrackingValue = (value = '') =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const trackEvent = ({
  name,
  buttonId,
  label,
  page = '',
  section = '',
  feature = '',
  eventGroup = 'engagement',
  eventCategory = '',
  additionalParams = {},
}) => {
  const normalizedId = slugifyTrackingValue(buttonId) || 'unknown_click';
  const normalizedName = slugifyTrackingValue(name) || 'interaction';
  const normalizedFeature = slugifyTrackingValue(feature) || 'general';
  const normalizedPage = slugifyTrackingValue(page) || 'unknown';
  const normalizedSection = slugifyTrackingValue(section) || 'general';
  const normalizedCategory = slugifyTrackingValue(eventCategory) || normalizedFeature;

  sendGAEvent({
    category: normalizedCategory,
    action: normalizedName,
    label: label ?? normalizedId,
    event_name: normalizedName,
    event_category: normalizedCategory,
    event_group: slugifyTrackingValue(eventGroup) || 'engagement',
    page: normalizedPage,
    section: normalizedSection,
    feature: normalizedFeature,
    button_id: normalizedId,
    custom_parameter_1: normalizedId,
    ...additionalParams,
  });
};

export const trackClickWithId = (category, buttonId, label, additionalParams = {}) => {
  trackEvent({
    name: `${slugifyTrackingValue(category)}_click`,
    buttonId,
    label,
    feature: category,
    eventCategory: category,
    additionalParams,
  });
};
